/**
 * The site's selected communities, read the one way both hosts read them.
 *
 * `content.community_profiles` is the one store (the Communities workspace writes it). The live
 * /communities route and the Studio preview both turn it into cards through this function, so the
 * order, the ten-card cap and what counts as a usable entry cannot differ between the two. Moved
 * here from apps/tenant-sites lib/community-profiles.ts, which re-exports it.
 */

export interface CommunityProfileDocument {
  id: string;
  title: string;
  url: string;
  kind: string;
}

export interface CommunityProfileGalleryItem {
  id: string;
  url: string;
  caption?: string;
}

export interface CommunityProfilePlaceItem {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
}

export interface CommunityProfile {
  id: string;
  slug: string;
  name: string;
  mls_search_name?: string | null;
  /** Grouped community: the union of phase names to match (parent + each subdivision). */
  mls_search_names?: string[];
  city?: string | null;
  county?: string | null;
  state?: string | null;
  description?: string | null;
  hero_image_url?: string | null;
  local_intro?: string | null;
  local_notes?: string | null;
  favorite_blurb?: string | null;
  hoa_fee_display?: string | null;
  hoa_rules?: string | null;
  highlights?: string[];
  listing_count_active?: number;
  zip_codes?: string[];
  is_featured?: boolean;
  sort_order?: number;
  documents?: CommunityProfileDocument[];
  gallery_images?: CommunityProfileGalleryItem[];
  schools?: CommunityProfilePlaceItem[];
  restaurants?: CommunityProfilePlaceItem[];
  parks?: CommunityProfilePlaceItem[];
  nearby_amenities?: CommunityProfilePlaceItem[];
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function normalizeDocuments(value: unknown): CommunityProfileDocument[] {
  if (!Array.isArray(value)) return [];
  return value.reduce<CommunityProfileDocument[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const record = item as Record<string, unknown>;
    const id = typeof record.id === 'string' ? record.id : '';
    const title = typeof record.title === 'string' ? record.title : '';
    const url = typeof record.url === 'string' ? record.url : '';
    if (!id || !title || !url) return acc;
    acc.push({
      id,
      title,
      url,
      kind: typeof record.kind === 'string' ? record.kind : 'document',
    });
    return acc;
  }, []);
}

function normalizeGallery(value: unknown): CommunityProfileGalleryItem[] {
  if (!Array.isArray(value)) return [];
  return value.reduce<CommunityProfileGalleryItem[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const record = item as Record<string, unknown>;
    const id = typeof record.id === 'string' ? record.id : '';
    const url = typeof record.url === 'string' ? record.url : '';
    if (!id || !url) return acc;
    acc.push({
      id,
      url,
      caption: typeof record.caption === 'string' ? record.caption : '',
    });
    return acc;
  }, []);
}

function normalizePlaces(value: unknown): CommunityProfilePlaceItem[] {
  if (!Array.isArray(value)) return [];
  return value.reduce<CommunityProfilePlaceItem[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const record = item as Record<string, unknown>;
    const id = typeof record.id === 'string' ? record.id : '';
    const name = typeof record.name === 'string' ? record.name : '';
    if (!id || !name) return acc;
    acc.push({
      id,
      name,
      description: typeof record.description === 'string' ? record.description : '',
      image_url: typeof record.image_url === 'string' ? record.image_url : '',
    });
    return acc;
  }, []);
}

/** `content.community_profiles` → the featured communities, in card order, at most ten. */
export function normalizeCommunityProfiles(value: unknown): CommunityProfile[] {
  const raw = Array.isArray(value) ? value : [];

  return raw
    .map<CommunityProfile | null>((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const record = entry as Record<string, unknown>;
      const id = typeof record.id === 'string' ? record.id : '';
      const slug = typeof record.slug === 'string' ? record.slug : '';
      const name = typeof record.name === 'string' ? record.name : '';
      if (!id || !slug || !name) return null;

      return {
        id,
        slug,
        name,
        mls_search_name: typeof record.mls_search_name === 'string' ? record.mls_search_name : null,
        mls_search_names: isStringArray(record.mls_search_names) ? record.mls_search_names : undefined,
        city: typeof record.city === 'string' ? record.city : null,
        county: typeof record.county === 'string' ? record.county : null,
        state: typeof record.state === 'string' ? record.state : null,
        description: typeof record.description === 'string' ? record.description : null,
        hero_image_url: typeof record.hero_image_url === 'string' ? record.hero_image_url : null,
        local_intro: typeof record.local_intro === 'string' ? record.local_intro : null,
        local_notes: typeof record.local_notes === 'string' ? record.local_notes : null,
        favorite_blurb: typeof record.favorite_blurb === 'string' ? record.favorite_blurb : null,
        hoa_fee_display: typeof record.hoa_fee_display === 'string' ? record.hoa_fee_display : null,
        hoa_rules: typeof record.hoa_rules === 'string' ? record.hoa_rules : null,
        highlights: isStringArray(record.highlights) ? record.highlights : [],
        listing_count_active: typeof record.listing_count_active === 'number' ? record.listing_count_active : 0,
        zip_codes: isStringArray(record.zip_codes) ? record.zip_codes : [],
        is_featured: Boolean(record.is_featured ?? true),
        sort_order: typeof record.sort_order === 'number' ? record.sort_order : 0,
        documents: normalizeDocuments(record.documents),
        gallery_images: normalizeGallery(record.gallery_images),
        schools: normalizePlaces(record.schools),
        restaurants: normalizePlaces(record.restaurants),
        parks: normalizePlaces(record.parks),
        nearby_amenities: normalizePlaces(record.nearby_amenities),
      };
    })
    .filter((entry): entry is CommunityProfile => Boolean(entry))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name))
    .slice(0, 10);
}

/** Bound the phase names passed to the MLS bucket. The match-gate cost grows with the name
 *  count; an empirical cliff (~16 names → 502) was observed historically, so cap below it.
 *  SGR's largest group (ABACOA) has 12 phases and resolves cold in ~6.5s, so 14 is safe. */
export const MAX_GROUP_SEARCH_NAMES = 14;

/**
 * For a (possibly grouped) community: the parent search name + the distinct phase names to ALSO
 * match. Pass `extra` as the `community_names` filter so the overview cards and the map search
 * combine ALL of a group's phases' listings.
 *
 * IMPORTANT: do NOT collapse phase names that "contain" the parent. The bucket's name-gate is
 * far stricter than a parent substring (verified: parent "ABACOA" alone returns 1 listing vs 8
 * with the phase names; "EVERGRENE" 0 vs 3) — so dropping those names drops real listings. We
 * only dedup (incl. the parent itself) and cap the count (the cache + warmer absorb the cold cost).
 */
export function communityGroupSearchNames(community: {
  name: string;
  mls_search_name?: string | null;
  mls_search_names?: string[];
}): { parent: string; extra: string[] } {
  const parent = community.mls_search_name || community.name;
  const seen = new Set<string>();
  const parentLc = (parent || '').toLowerCase().trim();
  if (parentLc) seen.add(parentLc);
  const extra: string[] = [];
  for (const raw of community.mls_search_names ?? []) {
    const name = (raw || '').trim();
    if (!name) continue;
    const lc = name.toLowerCase();
    if (seen.has(lc)) continue; // dedup, and skip a phase identical to the parent
    seen.add(lc);
    extra.push(name);
    if (extra.length >= MAX_GROUP_SEARCH_NAMES) break; // bound the match-gate cost
  }
  return { parent, extra };
}

/**
 * The geo-bounded listing search for one community card — the filters the live card and its
 * "View Listings" link both use. Always the GEO-bounded query. Deliberately no name-only fallback:
 * without a geo bound the gateway name-gate matches subdivisions statewide (Admirals Cove / Jupiter
 * surfaced a "Ports O Call" listing in North Palm Beach). The gateway widens zip -> county itself.
 */
export function communityListingFilters(community: CommunityProfile): Record<string, string> {
  // Grouped community: search the union of its phase names so the card combines ALL phases.
  const { parent, extra } = communityGroupSearchNames(community);
  const filters: Record<string, string> = { community: parent };
  if (extra.length) filters.community_names = extra.join(',');
  if (community.county) filters.county = community.county;
  if (community.city) filters.city = community.city;
  if (community.state) filters.state = community.state;
  if (community.zip_codes?.length) filters.zip_codes = community.zip_codes.join(',');
  return filters;
}

/** The card's "View Listings" link: the map search, carrying the grouped phase names. */
export function communityListingsHref(community: CommunityProfile): string {
  return `/listings?${new URLSearchParams(communityListingFilters(community)).toString()}`;
}
